import { addMonths, differenceInCalendarMonths, format, parseISO } from 'date-fns'
import { Decimal } from 'decimal.js'

import type {
  TSimulacaoGrupoIntermediariasPrice,
  TSimulacaoGrupoMensalPrice,
} from '@/features/simulador/brasil/domain/schemas/simulacao-price.schema'
import { CalculadoraJuros } from '@/features/simulador/shared/domain/calculadora-juros'
import { CalculadoraPeriodicidade } from '@/features/simulador/shared/domain/calculadora-periodicidade'
import { roundFloat } from '@/utils/number.utils'

export type TPeriodicidadePrice = TSimulacaoGrupoIntermediariasPrice['grupos'][number]['periodicidade']
type TGrupoIntermediariasPrice = TSimulacaoGrupoIntermediariasPrice['grupos'][number]

export class GerenciadorIntermediariasPrice {
  private preencherDatasIntermediarias(
    dataInicial: string,
    intervalo: number,
    dataUltimaMensal: string,
  ): TGrupoIntermediariasPrice['parcelas'] {
    if (intervalo <= 0) return []

    const inicio = parseISO(dataInicial)
    const dataLimite = parseISO(dataUltimaMensal)
    const parcelas: TGrupoIntermediariasPrice['parcelas'] = []

    let data = inicio
    while (data <= dataLimite) {
      parcelas.push({ data: format(data, 'yyyy-MM-dd') })
      data = addMonths(inicio, intervalo * parcelas.length)
    }

    return parcelas
  }

  criarGrupoInicial(
    datas: string[],
    valor: number = 0,
    percentual: number = 0,
    valorBase: number = 0,
  ): TSimulacaoGrupoIntermediariasPrice | undefined {
    if (!datas?.length || !valor || !percentual) return undefined

    const valorTotal = new Decimal(percentual).div(100).mul(valorBase).toNumber()
    const periodicidade = CalculadoraPeriodicidade.calcular(datas) as TPeriodicidadePrice
    const parcelas = datas.map((data) => ({ data }))

    return { percentual, valorTotal, grupos: [{ valor, quantidade: parcelas.length, periodicidade, parcelas }] }
  }

  criarNovoGrupo(
    percentual: number,
    valorBase: number,
    dataSimulacao: string,
    percentualJurosAnualPrice: number,
    mensais: TSimulacaoGrupoMensalPrice['parcelas'][number],
  ): TSimulacaoGrupoIntermediariasPrice {
    const valorTotal = new Decimal(percentual).div(100).mul(valorBase).toNumber()
    const periodicidade: TPeriodicidadePrice = 'Semestral'
    const dataPrimeiraIntermediaria = format(addMonths(parseISO(mensais.dataInicio!), 5), 'yyyy-MM-dd')
    const parcelas = this.preencherDatasIntermediarias(dataPrimeiraIntermediaria, 6, mensais.dataFim!)
    const mesesAtePagIntermediarias = differenceInCalendarMonths(
      parseISO(dataPrimeiraIntermediaria),
      parseISO(dataSimulacao),
    )

    const valorParcela = this.calcularPrestacaoComJuros(
      valorTotal,
      parcelas.length,
      mesesAtePagIntermediarias,
      percentualJurosAnualPrice,
      periodicidade,
    )

    return {
      percentual,
      valorTotal,
      grupos: [{ valor: valorParcela, quantidade: parcelas.length, periodicidade, parcelas }],
    }
  }

  calcularPrestacaoComJuros(
    saldoIntermediariasSemJuros: number,
    quantidade: number,
    mesesAtePagIntermediarias: number,
    percentualJurosAnualPrice: number,
    periodicidade: TPeriodicidadePrice,
  ): number {
    const calculadoraJuros = new CalculadoraJuros(percentualJurosAnualPrice)
    const valorIntermediariasSemJuros = roundFloat(
      new Decimal(saldoIntermediariasSemJuros).div(quantidade).toNumber(),
      2,
    )

    const saldoIntermediariasComJuros = calculadoraJuros.calcularValorFuturo(
      mesesAtePagIntermediarias,
      calculadoraJuros.conversaoTaxaAnualPara('Mensal'),
      valorIntermediariasSemJuros * quantidade,
    )

    return roundFloat(
      calculadoraJuros.calcularPrestacao(
        quantidade,
        calculadoraJuros.conversaoTaxaAnualPara(periodicidade),
        saldoIntermediariasComJuros,
      ),
      2,
    )
  }

  calcularAjustePorData(
    grupo: TGrupoIntermediariasPrice,
    novaData: string,
    percentual: number,
    valorProposto: number,
    dataSimulacao: string,
    percentualJurosAnualPrice: number,
    dataUltimaMensal: string,
  ): { valor: number; valorTotal: number; parcelas: TGrupoIntermediariasPrice['parcelas']; quantidade: number } {
    const saldoSemJuros = new Decimal(percentual).div(100).mul(valorProposto).toNumber()
    const mesesAtePagIntermediarias = differenceInCalendarMonths(parseISO(novaData), parseISO(dataSimulacao))

    const intervalo = CalculadoraPeriodicidade.valorDaPeriodicidade[grupo.periodicidade]
    const parcelas = this.preencherDatasIntermediarias(novaData, intervalo, dataUltimaMensal)
    const quantidade = parcelas.length

    const valor = this.calcularPrestacaoComJuros(
      saldoSemJuros,
      quantidade,
      mesesAtePagIntermediarias,
      percentualJurosAnualPrice,
      grupo.periodicidade,
    )

    return { valor, valorTotal: saldoSemJuros, parcelas, quantidade }
  }

  calcularAjustePorPeriodicidade(
    grupo: TGrupoIntermediariasPrice,
    novaPeriodicidade: TPeriodicidadePrice,
    percentual: number,
    valorProposto: number,
    dataSimulacao: string,
    percentualJurosAnualPrice: number,
    dataUltimaMensal: string,
  ): { valor: number; valorTotal: number; parcelas: TGrupoIntermediariasPrice['parcelas']; quantidade: number } {
    const primeiraData = grupo.parcelas[0]!.data!
    const saldoSemJuros = new Decimal(percentual).div(100).mul(valorProposto).toNumber()
    const mesesAtePagIntermediarias = differenceInCalendarMonths(parseISO(primeiraData), parseISO(dataSimulacao))

    const intervalo = CalculadoraPeriodicidade.valorDaPeriodicidade[novaPeriodicidade]
    const parcelas = this.preencherDatasIntermediarias(primeiraData, intervalo, dataUltimaMensal)
    const quantidade = parcelas.length

    const valor = this.calcularPrestacaoComJuros(
      saldoSemJuros,
      quantidade,
      mesesAtePagIntermediarias,
      percentualJurosAnualPrice,
      novaPeriodicidade,
    )

    return { valor, valorTotal: saldoSemJuros, parcelas, quantidade }
  }

  calcularAjustePorPercentual(
    intermediarias: TSimulacaoGrupoIntermediariasPrice,
    novoPercentual: number,
    valorProposto: number,
    dataSimulacao: string,
    percentualJurosAnualPrice: number,
  ): TSimulacaoGrupoIntermediariasPrice {
    const grupo = intermediarias.grupos[0]
    const primeiraParcela = grupo?.parcelas[0]
    if (!primeiraParcela?.data || !grupo) return intermediarias

    const saldoSemJuros = new Decimal(novoPercentual).div(100).mul(valorProposto).toNumber()
    const mesesAtePagIntermediarias = differenceInCalendarMonths(
      parseISO(primeiraParcela.data),
      parseISO(dataSimulacao),
    )
    const valor = this.calcularPrestacaoComJuros(
      saldoSemJuros,
      grupo.quantidade,
      mesesAtePagIntermediarias,
      percentualJurosAnualPrice,
      grupo.periodicidade,
    )

    return { percentual: novoPercentual, valorTotal: saldoSemJuros, grupos: [{ ...grupo, valor }] }
  }

  calcularAjustePorValorProposto(
    intermediarias: TSimulacaoGrupoIntermediariasPrice,
    novoValorProposto: number,
    dataSimulacao: string,
    percentualJurosAnualPrice: number,
  ): TSimulacaoGrupoIntermediariasPrice {
    const grupo = intermediarias.grupos[0]
    const primeiraParcela = grupo?.parcelas[0]
    if (!primeiraParcela?.data || !grupo) return intermediarias

    const saldoSemJuros = new Decimal(intermediarias.percentual).div(100).mul(novoValorProposto).toNumber()
    const mesesAtePagIntermediarias = differenceInCalendarMonths(
      parseISO(primeiraParcela.data),
      parseISO(dataSimulacao),
    )
    const valor = this.calcularPrestacaoComJuros(
      saldoSemJuros,
      grupo.quantidade,
      mesesAtePagIntermediarias,
      percentualJurosAnualPrice,
      grupo.periodicidade,
    )

    return { percentual: intermediarias.percentual, valorTotal: saldoSemJuros, grupos: [{ ...grupo, valor }] }
  }
}
